import { Bars3Icon, BellIcon, MagnifyingGlassIcon, PowerIcon } from '@heroicons/react/24/outline';
import { ThemeToggle } from './ThemeToggle';
import { useAuth } from '../../lib/auth';

interface TopBarProps {
  onToggleSidebar: () => void;
}

export function TopBar({ onToggleSidebar }: TopBarProps): JSX.Element {
  const { user, logout } = useAuth();

  const initials =
    user?.name
      ?.split(' ')
      .map((part) => part[0] ?? '')
      .join('')
      .slice(0, 2)
      .toUpperCase() ?? 'IN';

  const roleLabel =
    user?.role === 'admin' ? 'Organization Admin' : user?.role === 'agent' ? 'Team Member' : 'Viewer';

  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl">
      <div className="glass border-b border-white/10 px-4 py-3 shadow-glass">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="focus-ring inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/60 text-foreground sm:hidden"
              onClick={onToggleSidebar}
              aria-label="Toggle navigation"
            >
              <Bars3Icon className="h-5 w-5" />
            </button>
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-muted-foreground">InspectAI</p>
              <h1 className="text-xl font-semibold">Property Inspection Studio</h1>
            </div>
          </div>

          <div className="flex flex-1 items-center justify-end gap-4">
            <div className="hidden max-w-md flex-1 items-center rounded-full border border-white/10 bg-white/30 px-3 py-2 shadow-inner backdrop-blur-md dark:bg-white/5 sm:flex">
              <MagnifyingGlassIcon className="mr-2 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search inspections, properties, or actions"
                className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
            </div>

            <ThemeToggle />

            <button
              type="button"
              className="focus-ring relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/40 text-foreground hover:bg-white/60 dark:border-white/10 dark:bg-white/10 dark:hover:bg-white/20"
              aria-label="Notifications"
            >
              <BellIcon className="h-5 w-5" />
              <span className="absolute right-0 top-0 inline-flex h-2 w-2 rounded-full bg-accent" />
            </button>

            <div className="glass-card hidden items-center gap-3 px-3 py-2 sm:flex">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary/60 to-accent/60 text-sm font-semibold text-white">
                {initials}
              </div>
              <div className="text-left">
                <p className="text-sm font-medium leading-none">{user?.name ?? 'InspectAI user'}</p>
                <p className="text-xs text-muted-foreground">{roleLabel}</p>
              </div>
              <button
                type="button"
                onClick={logout}
                className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/10 text-muted-foreground transition hover:text-destructive"
                aria-label="Sign out"
              >
                <PowerIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
